import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { ConfirmModal } from '../ConfirmModal'
import { IconButton } from '../IconButton'
import { Modal } from '../Modal'
import { SortableTh } from '../SortableTh'
import { useTableSort } from '../../hooks/useTableSort'
import { importarFornecedores } from '../../lib/fornecedorService'
import { downloadFornecedorTemplateXlsx, parseFornecedorFile } from '../../lib/parseFornecedorImport'
import { supabase } from '../../lib/supabase'
import type { Fornecedor } from '../../types/database'
import { TIPO_PESSOA_LABELS } from '../../types/database'
import { FornecedorFormModal } from './FornecedorFormModal'
import type { FornecedorImportRow } from '../../lib/fornecedorService'

interface FornecedoresSectionProps {
  embedded?: boolean
  onInfo?: (message: string) => void
  onError?: (message: string) => void
}

export function FornecedoresSection({ embedded = false, onInfo, onError }: FornecedoresSectionProps) {
  const { canWrite, canDelete } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtro, setFiltro] = useState('')
  const [modalOpen, setModalOpenRaw] = useState(() => {
    try {
      return sessionStorage.getItem('isen-educae:ui:fornecedor-modal') === '1'
    } catch {
      return false
    }
  })
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editing, setEditing] = useState<Fornecedor | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<Fornecedor | null>(null)
  const [importPreview, setImportPreview] = useState<FornecedorImportRow[] | null>(null)
  const [importFileName, setImportFileName] = useState('')
  const [importando, setImportando] = useState(false)
  const tableSort = useTableSort<Fornecedor>('razao_social', 'asc')

  function setModalOpen(open: boolean, persistCreate = false) {
    setModalOpenRaw(open)
    try {
      if (open && persistCreate) sessionStorage.setItem('isen-educae:ui:fornecedor-modal', '1')
      else sessionStorage.removeItem('isen-educae:ui:fornecedor-modal')
    } catch {
      /* ignore */
    }
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(undefined)
  }

  const notifyError = (msg: string) => {
    setError(msg)
    onError?.(msg)
  }

  const notifyInfo = (msg: string) => {
    setError(null)
    onInfo?.(msg)
  }

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('fornecedores')
      .select('*')
      .order('razao_social')
    if (err) notifyError(err.message)
    else setFornecedores((data as Fornecedor[]) || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filteredSorted = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    const list = q
      ? fornecedores.filter(
          (f) =>
            f.razao_social.toLowerCase().includes(q) ||
            (f.nome_fantasia || '').toLowerCase().includes(q) ||
            (f.cpf_cnpj || '').includes(q) ||
            (f.cidade || '').toLowerCase().includes(q),
        )
      : fornecedores

    const accessors: Record<string, (f: Fornecedor) => string | number> = {
      razao_social: (f) => f.razao_social,
      nome_fantasia: (f) => f.nome_fantasia || '',
      tipo_pessoa: (f) => f.tipo_pessoa,
      cpf_cnpj: (f) => f.cpf_cnpj || '',
      cidade: (f) => f.cidade || '',
      responsavel: (f) => f.responsavel_nome || '',
      email: (f) => f.email || '',
      ativo: (f) => (f.ativo ? 1 : 0),
    }
    return tableSort.sortRows(list, accessors)
  }, [fornecedores, filtro, tableSort])

  function openCreate() {
    setModalMode('create')
    setEditing(undefined)
    setModalOpen(true, true)
  }

  function openEdit(f: Fornecedor) {
    setModalMode('edit')
    setEditing(f)
    setModalOpen(true)
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    const { error: err } = await supabase.from('fornecedores').delete().eq('id', deleteTarget.id)
    if (err) notifyError(err.message)
    else {
      notifyInfo('Fornecedor excluído.')
      setDeleteTarget(null)
      load()
    }
  }

  async function onImportFile(file: File) {
    setImportando(true)
    setError(null)
    try {
      const rows = await parseFornecedorFile(file)
      if (!rows.length) {
        notifyError('Nenhuma linha encontrada no arquivo.')
        return
      }
      setImportPreview(rows)
      setImportFileName(file.name)
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Erro ao ler arquivo.')
    } finally {
      setImportando(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function confirmarImportacao() {
    if (!importPreview?.length) return
    setImportando(true)
    try {
      const { ok, erros } = await importarFornecedores(importPreview)
      setImportPreview(null)
      setImportFileName('')
      if (erros.length) {
        notifyError(`${ok} importado(s). Erros:\n${erros.slice(0, 8).join('\n')}`)
      } else {
        notifyInfo(`${ok} fornecedor(es) importado(s) com sucesso.`)
      }
      load()
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'Erro ao importar.')
    } finally {
      setImportando(false)
    }
  }

  const writable = canWrite('fornecedores')
  const previewValid = importPreview?.filter((r) => !r.erro).length ?? 0
  const previewInvalid = importPreview?.filter((r) => r.erro).length ?? 0

  if (loading && !fornecedores.length && !embedded) {
    return <div className="loading">Carregando fornecedores…</div>
  }

  return (
    <div className={embedded ? '' : 'card'}>
      <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
        <div>
          <h2 style={{ fontSize: 16, margin: 0 }}>Fornecedores</h2>
          <p className="kpi-sub" style={{ margin: '4px 0 0' }}>
            Cadastro de fornecedores com identificação, endereço e contatos. Importe planilhas XLSX ou CSV.
          </p>
        </div>
        {writable && (
          <div className="table-actions">
            <button type="button" className="btn btn-secondary" onClick={downloadFornecedorTemplateXlsx}>
              Baixar modelo XLSX
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={importando}
            >
              {importando ? 'Lendo…' : 'Importar XLSX/CSV'}
            </button>
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              Novo fornecedor
            </button>
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onImportFile(file)
        }}
      />

      {error && !embedded && <div className="alert alert-error">{error}</div>}

      <div className="field" style={{ maxWidth: 360, marginBottom: 12 }}>
        <label htmlFor="forn-filtro">Buscar</label>
        <input
          id="forn-filtro"
          placeholder="Razão social, CNPJ, cidade…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh
                label="Razão social"
                sortKey="razao_social"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="name"
              />
              <SortableTh
                label="Nome fantasia"
                sortKey="nome_fantasia"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Tipo"
                sortKey="tipo_pessoa"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="compact"
              />
              <SortableTh
                label="CPF/CNPJ"
                sortKey="cpf_cnpj"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Cidade"
                sortKey="cidade"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Responsável"
                sortKey="responsavel"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="E-mail"
                sortKey="email"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="text"
              />
              <SortableTh
                label="Ativo"
                sortKey="ativo"
                activeKey={tableSort.sortKey}
                direction={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                col="compact"
              />
              <th className="col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((f) => (
              <tr key={f.id}>
                <td className="col-name">{f.razao_social}</td>
                <td className="col-text">{f.nome_fantasia || '—'}</td>
                <td className="col-compact">{TIPO_PESSOA_LABELS[f.tipo_pessoa]}</td>
                <td className="col-text">{f.cpf_cnpj || '—'}</td>
                <td className="col-text">{f.cidade ? `${f.cidade}${f.uf ? `/${f.uf}` : ''}` : '—'}</td>
                <td className="col-text">{f.responsavel_nome || '—'}</td>
                <td className="col-text">{f.email || '—'}</td>
                <td className="col-compact">{f.ativo ? 'Sim' : 'Não'}</td>
                <td className="col-actions">
                  <div className="table-actions">
                    {writable && (
                      <IconButton
                        variant="edit"
                        label={`Editar ${f.razao_social}`}
                        onClick={() => openEdit(f)}
                      />
                    )}
                    {canDelete('fornecedores') && (
                      <IconButton
                        variant="delete"
                        label={`Excluir ${f.razao_social}`}
                        onClick={() => setDeleteTarget(f)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!filteredSorted.length && (
              <tr>
                <td colSpan={9} className="kpi-sub">
                  Nenhum fornecedor cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <FornecedorFormModal
        open={modalOpen}
        mode={modalMode}
        fornecedor={editing}
        onClose={closeModal}
        onSaved={() => {
          notifyInfo(modalMode === 'edit' ? 'Fornecedor atualizado.' : 'Fornecedor cadastrado.')
          load()
        }}
        onError={notifyError}
      />

      <Modal
        open={!!importPreview}
        title="Pré-visualização da importação"
        size="xl"
        onClose={() => {
          setImportPreview(null)
          setImportFileName('')
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setImportPreview(null)
                setImportFileName('')
              }}
              disabled={importando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmarImportacao}
              disabled={importando || previewValid === 0}
            >
              {importando ? 'Importando…' : `Importar ${previewValid} registro(s)`}
            </button>
          </>
        }
      >
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Arquivo: <strong>{importFileName}</strong> — {previewValid} válido(s), {previewInvalid}{' '}
          com erro(s).
        </p>
        <div className="table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Linha</th>
                <th>Razão social</th>
                <th>CPF/CNPJ</th>
                <th>Cidade</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {importPreview?.map((r) => (
                <tr key={r.linha}>
                  <td>{r.linha}</td>
                  <td>{r.razao_social || '—'}</td>
                  <td>{r.cpf_cnpj || '—'}</td>
                  <td>{r.cidade || '—'}</td>
                  <td>{r.erro ? <span className="badge badge-critico">{r.erro}</span> : 'OK'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        title="Excluir fornecedor"
        description={
          deleteTarget
            ? `Excluir "${deleteTarget.razao_social}"? Pedidos mensais vinculados podem impedir a exclusão.`
            : ''
        }
        confirmLabel="Excluir"
        variant="danger"
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}
